// ==========================================================================
// REPORTS & TAX/SSO DOCUMENTS MODULE
// WuRuPayroll - Bank transfer summary, SSO report, Withholding tax, กท.26 ก & Print
// ==========================================================================

import {
  getTimesheet,
  saveTimesheet,
  uploadTransferSlip,
  cleanGarbageTimesheets
} from '../firebase-db.js';

import { escapeHtml, escapeAttr, escapeJsParam } from '../utils/sanitize.js';
import { toast, showConfirm } from '../utils/toast.js';
import {
  calculatePayrollForEmployee,
  convertToThaiBahtText,
  isEmployeeActiveInMonth,
  getEmployeeDeductionSettings
} from './payroll-calc.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

const canViewSalary = (emp) => {
  if (typeof window !== 'undefined' && window.canViewSalary) {
    return window.canViewSalary(emp);
  }
  return true;
};

// ==========================================================================
// REPORTS PAGE (Requirement 1, 11, 13, 15, 24)
// ==========================================================================
function renderReportsPage() {
  document.getElementById('page-display-title').textContent = 'รายงานเงินเดือนและภาษี';
  document.getElementById('page-display-desc').textContent = `สรุปส่งเงินโอนพนักงาน ส่งประกันสังคม ภาษี ณ ที่จ่าย และแบบ กท.26 ก ของบริษัท ${state.activeCompanySettings.name}`;

  const wrapper = document.getElementById('page-content-wrapper');

  wrapper.innerHTML = `
    <!-- Top Selector and Tab Category -->
    <div class="glass-card" style="margin-bottom: 25px;">
      <div class="calendar-view-header" style="margin-bottom:0;">
        <h3 style="font-weight:700; color:var(--primary-dark);">เลือกหัวข้อรายงาน</h3>
        ${window.checkPermission('export_transfer_report') || window.checkPermission('export_sso_report') || window.checkPermission('export_tax_report') || window.checkPermission('export_kt26k_report') ? `
        <div class="calendar-modes">
          ${window.checkPermission('export_transfer_report') ? `<button class="calendar-mode-btn active" id="btn-rep-bank" onclick="switchReportTab('bank')">🏦 สรุปโอนเงิน</button>` : ''}
          ${window.checkPermission('export_sso_report') ? `<button class="calendar-mode-btn" id="btn-rep-ss" onclick="switchReportTab('ss')">🏥 รายงานประกันสังคม</button>` : ''}
          ${window.checkPermission('export_tax_report') ? `<button class="calendar-mode-btn" id="btn-rep-tax" onclick="switchReportTab('tax')">🧾 ภาษีหัก ณ ที่จ่าย</button>` : ''}
          ${window.checkPermission('export_kt26k_report') ? `<button class="calendar-mode-btn" id="btn-rep-kt26" onclick="switchReportTab('kt26')">📋 กท.26 ก (ประกันสังคมประจำปี)</button>` : ''}
        </div>
      ` : ''}
      </div>
    </div>

    <!-- Active Report container -->
    <div id="report-view-container"></div>
  `;

  // Defaults
  let activeTab = state.activeReportTab || 'bank';
  const hasBank = window.checkPermission('export_transfer_report');
  const hasSSO = window.checkPermission('export_sso_report');
  const hasTax = window.checkPermission('export_tax_report');
  const hasKT26 = window.checkPermission('export_kt26k_report');

  const checkAccess = (tab) => {
    if (tab === 'bank' && hasBank) return true;
    if (tab === 'ss' && hasSSO) return true;
    if (tab === 'tax' && hasTax) return true;
    if (tab === 'kt26' && hasKT26) return true;
    return false;
  };

  if (!checkAccess(activeTab)) {
    if (hasBank) activeTab = 'bank';
    else if (hasSSO) activeTab = 'ss';
    else if (hasTax) activeTab = 'tax';
    else if (hasKT26) activeTab = 'kt26';
    else activeTab = null;
  }

  if (activeTab) {
    switchReportTab(activeTab);
  } else {
    document.getElementById('report-view-container').innerHTML = '<div class="glass-card text-center" style="padding:40px;"><h4 style="color:var(--text-sub);">คุณไม่มีสิทธิ์เข้าถึงหน้ารายงานใดๆ</h4></div>';
  }
}

function switchReportTab(tabName) {
  if (tabName === 'bank' && !window.checkPermission('export_transfer_report')) { alert('ไม่มีสิทธิ์เข้าถึงรายงานนี้'); return; }
  if (tabName === 'ss' && !window.checkPermission('export_sso_report')) { alert('ไม่มีสิทธิ์เข้าถึงรายงานนี้'); return; }
  if (tabName === 'tax' && !window.checkPermission('export_tax_report')) { alert('ไม่มีสิทธิ์เข้าถึงรายงานนี้'); return; }
  if (tabName === 'kt26' && !window.checkPermission('export_kt26k_report')) { alert('ไม่มีสิทธิ์เข้าถึงรายงานนี้'); return; }

  state.activeReportTab = tabName;
  // Toggle active button
  document.getElementById('btn-rep-bank')?.classList.remove('active');
  document.getElementById('btn-rep-ss')?.classList.remove('active');
  document.getElementById('btn-rep-tax')?.classList.remove('active');
  document.getElementById('btn-rep-kt26')?.classList.remove('active');

  const container = document.getElementById('report-view-container');
  if (!container) return;

  if (tabName === 'bank') {
    document.getElementById('btn-rep-bank')?.classList.add('active');
    renderBankSummaryReport(container);
  } else if (tabName === 'ss') {
    document.getElementById('btn-rep-ss')?.classList.add('active');
    renderSocialSecurityMonthlyReport(container);
  } else if (tabName === 'tax') {
    document.getElementById('btn-rep-tax')?.classList.add('active');
    renderWithholdingTaxReport(container);
  } else {
    document.getElementById('btn-rep-kt26')?.classList.add('active');
    renderKT26AnnualReport(container);
  }
};

// Report Tab 1: Bank transfer and copy slips to Clipboard (Requirement 11)
async function renderBankSummaryReport(container) {
  container.innerHTML = `<div class="glass-card"><h4>กำลังคำนวณข้อมูลสรุปเงินโอนสิ้นเดือน...</h4></div>`;

  let totalTransferAmt = 0;
  let summaryRowsHTML = '';

  // Clipboard text builder
  let clipboardText = `📊 สรุปยอดจ่ายเงินเดือนบริษัท ${state.activeCompanySettings.name} ประจำเดือน ${state.selectedMonth}\n`;
  clipboardText += `-----------------------------------------------\n`;

  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));

  if (activeEmps.length === 0) {
    container.innerHTML = `
      <div class="glass-card text-center">
        <h4 style="color:var(--text-sub);">ไม่พบข้อมูลลูกน้องที่มีการทำรายการ</h4>
      </div>
    `;
    return;
  }

  const currentMonthStr = new Date().toISOString().slice(0, 7);
  const visibleBankEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const bankTimesheets = await Promise.all(visibleBankEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, state.selectedMonth)
  })));

  for (const { emp, ts } of bankTimesheets) {
    if (ts.isPhantom && state.selectedMonth !== currentMonthStr) continue;
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

    totalTransferAmt += summ.netSalary;

    clipboardText += `🔹 ${emp.realName} (${emp.nickname})\n`;
    if (emp.bankAccount) {
      if (emp.bankName) clipboardText += `   🏛️ ธนาคาร: ${emp.bankName}\n`;
      clipboardText += `   💳 บัญชี: ${emp.bankAccount}\n`;
    } else {
      clipboardText += `   💵 รูปแบบจ่าย: รับเงินสด\n`;
    }
    clipboardText += `   💰 ยอดรับสุทธิ: ${summ.netSalary.toLocaleString()} บาท\n\n`;

    // Slips upload status
    const slipHint = ts.transferSlipUrl ?
      `<a href="${ts.transferSlipUrl}" target="_blank" style="color:var(--success); font-weight:700;">✅ ดูสลิปหลักฐาน</a>` :
      (window.checkPermission('edit_timesheet') ? `
        <div style="display:flex; flex-direction:column; gap:5px;">
          <input type="file" id="slip-file-${emp.id}" style="font-size:0.75rem; width:130px; padding:2px;">
          <button class="btn btn-secondary btn-sm" style="padding:2px 6px; font-size:0.72rem;" onclick="uploadSlipTrigger('${escapeJsParam(emp.id)}')">💾 อัปโหลดสลิป (.webp)</button>
        </div>
      ` : '🔒 ยังไม่ได้จ่าย');

    const bankDisplay = emp.bankAccount ? `<div>${emp.bankName ? `<span class="badge badge-info" style="font-size:0.75rem; margin-bottom:3px; display:inline-block;">${escapeHtml(emp.bankName)}</span><br>` : ''}<code>${escapeHtml(emp.bankAccount)}</code></div>` : '<span style="color:var(--danger);">💸 รับเงินสด</span>';

    summaryRowsHTML += `
      <tr>
        <td><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</td>
        <td>${bankDisplay}</td>
        <td><strong>${(summ.netSalary).toLocaleString()} บาท</strong></td>
        <td>${slipHint}</td>
        <td>
          <button class="btn btn-secondary btn-sm" onclick="showPayslipModal('${escapeJsParam(emp.id)}')">🖨️ ออกสลิปเงินเดือน PDF</button>
        </td>
      </tr>
    `;
  }

  clipboardText += `-----------------------------------------------\n`;
  clipboardText += `💰 ยอดรวมทำรายการโอนรวมสิ้นเดือน: ${totalTransferAmt.toLocaleString()} บาท\n`;
  clipboardText += `โปรดดำเนินการทำจ่ายตามรายการข้างต้นด้วย`;

  // Stringify for clipboard safely
  state.cachedClipboard = clipboardText;

  container.innerHTML = `
    <div class="glass-card" style="display:flex; flex-direction:column; gap:20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:15px;">
        <h4 style="font-weight:700; color:var(--primary-dark);">สรุปการสั่งจ่ายเงินโอนธนาคาร</h4>
        <div style="display:flex; gap:10px;">
          ${window.checkPermission('export_transfer_report') ? `<button class="btn btn-secondary" onclick="printTransferSummaryReport()">🖨️ พิมพ์รายงาน</button>` : ''}
          <button class="btn btn-primary" onclick="copyFinanceSummaryToClipboard()">📋 คัดลอกรายงานสรุป (ส่งไลน์การเงิน)</button>
        </div>
      </div>

      <!-- Authorized Payor Selection (Required before issuing/printing payslips) -->
      <div style="background: rgba(0, 0, 0, 0.03); border: 1px solid var(--gray-300); padding: 15px; border-radius: var(--border-radius-md); display: flex; align-items: center; gap: 15px; flex-wrap: wrap;">
        <div style="display:flex; align-items:center; gap:10px;">
          <label style="font-weight:700; font-size:0.9rem; color:var(--primary-dark); white-space:nowrap;">👤 ผู้อนุมัติสั่งจ่าย:</label>
          <select id="selected-payor-dropdown" style="padding: 8px 12px; border-radius: var(--border-radius-sm); border: 1px solid var(--gray-300); font-size:0.85rem;" onchange="updateSelectedPayor(this.value)">
            <option value="">-- โปรดเลือกผู้อนุมัติก่อนออกสลิป --</option>
            ${(state.activeCompanySettings.authorizedPayors || []).map(p => `<option value="${p}" ${state.selectedPayor === p ? 'selected' : ''}>${p}</option>`).join('')}
          </select>
        </div>
        <button class="btn btn-secondary btn-sm" onclick="showManagePayorsModal()" style="padding: 8px 14px;">⚙️ จัดการรายชื่อผู้อนุมัติ</button>
      </div>

      <div class="table-responsive">
        <table class="payroll-table">
          <thead>
            <tr>
              <th>ชื่อ - นามสกุล (ลูกน้อง)</th>
              <th>เลขบัญชีธนาคารปลายทาง</th>
              <th>ยอดเงินที่ต้องโอนสุทธิ</th>
              <th>หลักฐานเงินโอน (.webp)</th>
              <th>สลิปเงินเดือน</th>
            </tr>
          </thead>
          <tbody>
            ${summaryRowsHTML}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

async function cleanGarbageTimesheetsTrigger() {
  if (!confirm("ระบบจะสแกนหาข้อมูลเงินเดือนที่เป็นเดือนในอดีต มียอดเงิน 0 บาท และไม่มีการแก้ไขใดๆ เพื่อลบทิ้ง\n\nคุณแน่ใจหรือไม่ที่จะเริ่มดำเนินการ?")) return;

  try {
    const btn = event.target;
    const oldText = btn.innerHTML;
    btn.innerHTML = '⏳ กำลังสแกนและล้างข้อมูล...';
    btn.disabled = true;

    const count = await cleanGarbageTimesheets();

    btn.innerHTML = oldText;
    btn.disabled = false;
    toast.success(`ดำเนินการล้างข้อมูลขยะเสร็จสิ้น! ลบข้อมูลที่ว่างเปล่าออกไปทั้งหมด ${count} รายการ`);
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการล้างข้อมูลขยะ: " + err.message);
  }
};

function copyFinanceSummaryToClipboard() {
  if (!state.cachedClipboard) return;
  navigator.clipboard.writeText(state.cachedClipboard)
    .then(() => toast.success("📋 คัดลอกรายงานสรุปเงินโอนลงคลิปบอร์ดสำเร็จ! สามารถนำไปวางส่งไลน์ (Line) ให้การเงินได้ทันที"))
    .catch(err => {
      console.error("Could not copy text", err);
      toast.error("ไม่สามารถคัดลอกข้อความได้");
    });
};

async function uploadSlipTrigger(empId) {
  const fileInput = document.getElementById(`slip-file-${empId}`);
  const file = fileInput?.files[0];
  if (!file) {
    toast.warning("กรุณาเลือกไฟล์รูปภาพหลักฐานการจ่ายเงินก่อนอัปโหลด");
    return;
  }

  try {
    toast.info("⏳ กำลังเริ่มอัปโหลดไฟล์หลักฐานและบีบอัดเป็น WebP ประสิทธิภาพสูง...");
    const url = await uploadTransferSlip(empId, state.selectedMonth, file);
    toast.success("✅ อัปโหลดและบันทึกสลิปสำเร็จ!");
    switchReportTab('bank'); // Reload report
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการอัปโหลดรูปภาพสลิป: " + (err.message || ''));
  }
};

// (convertToThaiBahtText modularized into ./modules/payroll-calc.js)

// Render Individual Payslip standard modal printable on screen (Requirement 1)
async function showPayslipModal(empId) {
  state.activePayslipEmployeeId = empId;

  const emp = state.employees.find(e => e.id === empId);
  const ts = await getTimesheet(empId, state.selectedMonth);

  // Requirement: block payslip printing/generation if timesheet is not locked
  if (!ts.isLocked) {
    toast.warning(`ไม่สามารถออกสลิปเงินเดือนได้ เนื่องจากข้อมูลเงินเดือนของ ${emp ? emp.realName : 'พนักงาน'} ประจำงวดเดือน ${state.selectedMonth} ยังไม่ได้ทำการล็อค (Lock) ข้อมูล!\n\nกรุณาตรวจสอบความถูกต้องและกดยืนยันและล็อคข้อมูลเงินเดือนให้เรียบร้อยก่อน จึงจะสามารถออกสลิปเงินเดือนได้`, "ยังไม่ได้ล็อคข้อมูล");
    return;
  }

  // Requirement: block payslip printing/generation if payor is not selected
  if (!state.selectedPayor) {
    toast.warning("ระบบยังไม่อนุญาตให้ออกสลิปเงินเดือนจนกว่าคุณจะระบุรายชื่อผู้อนุมัติสั่งจ่ายเงิน!\n\nโปรดเลือกรายชื่อผู้อนุมัติจากกล่องรายการในหน้ารายงานสรุปโอนเงินก่อน หากยังไม่มีรายชื่อ สามารถกดปุ่ม 'จัดการรายชื่อผู้อนุมัติ' เพื่อเพิ่มได้ทันที", "ยังไม่ได้เลือกผู้อนุมัติ");
    return;
  }

  const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

  let modalOverlay = document.getElementById('payslip-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'payslip-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  const [y, m] = state.selectedMonth.split('-');
  const thaiMonthStr = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
  const lastDay = new Date(parseInt(y), parseInt(m), 0);
  const payDateStr = lastDay.toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' });

  const comp = state.activeCompanySettings;
  const compAddress = comp.address || 'เลขที่ - ถนน - แขวง - เขต กรุงเทพมหานคร';
  const compTaxId = comp.taxId || '-';
  const compPhone = comp.phone || '-';
  const thaiBahtNetText = convertToThaiBahtText(summ.netSalary);

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width:800px;">
      <div class="modal-header">
        <h3>สลิปเงินเดือน</h3>
        <button class="btn-close-modal" onclick="document.getElementById('payslip-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body" id="printable-payslip-area" style="background:#fff; color:#000; padding:45px 50px; font-family:'Sarabun', sans-serif;">
        <!-- Header Structure -->
        <div style="display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px double #000; padding-bottom:15px; margin-bottom:20px;">
          <div style="text-align:left; max-width:60%;">
            <h3 style="font-weight:700; margin:0 0 5px 0; font-size:1.15rem; color:#000;">${comp.name}</h3>
            <p style="margin:0 0 3px 0; font-size:0.78rem; line-height:1.4; color:#333;"><strong>ที่ตั้ง:</strong> ${compAddress}</p>
            <p style="margin:0; font-size:0.78rem; color:#333;"><strong>โทร:</strong> ${compPhone} &nbsp;|&nbsp; <strong>เลขประจำตัวผู้เสียภาษี:</strong> ${compTaxId}</p>
          </div>
          <div style="text-align:right;">
            <h2 style="font-weight:800; margin:0 0 5px 0; font-size:1.4rem; color:#000; letter-spacing:0.5px;">ใบแจ้งยอดเงินเดือน / Payslip</h2>
            <p style="margin:0 0 3px 0; font-size:0.8rem; color:#333;"><strong>งวดการจ่ายเงินประจำ:</strong> ${thaiMonthStr}</p>
            <p style="margin:0; font-size:0.8rem; color:#333;"><strong>วันที่จ่ายเงิน:</strong> ${payDateStr}</p>
          </div>
        </div>

        <!-- Employee Info Section -->
        <div style="display:grid; grid-template-columns:1.2fr 1fr; gap:12px 25px; margin-bottom:20px; font-size:0.82rem; border:1px solid #ccc; padding:15px; border-radius:6px; background:#fcfcfc;">
          <div><strong>ชื่อพนักงาน:</strong> ${escapeHtml(emp.realName)}</div>
          <div><strong>เลขบัตรประจำตัวประชาชน:</strong> ${escapeHtml(emp.citizenId)}</div>
          <div><strong>ตำแหน่ง:</strong> ${escapeHtml(emp.position || '-')}</div>
          <div><strong>แผนก:</strong> ${escapeHtml(emp.department || '-')}</div>
          <div><strong>วันที่เริ่มปฏิบัติงาน:</strong> ${escapeHtml(emp.startDate)}</div>
          <div><strong>รูปแบบการจ่าย:</strong> ${emp.bankAccount ? `โอนผ่านธนาคาร (${emp.bankName ? escapeHtml(emp.bankName) + ' ' : ''}เลขที่: ${escapeHtml(emp.bankAccount)})` : 'รับเงินสด'}</div>
        </div>

        <!-- Wages & Deductions Grid Table -->
        <div style="display:flex; justify-content:space-between; gap:0; font-size:0.85rem; border:1px solid #000; border-radius:6px; overflow:hidden; margin-bottom:20px;">
          <!-- Left Column (Earnings) -->
          <div style="flex:1; border-right:1px solid #000; display:flex; flex-direction:column; justify-content:space-between;">
            <div>
              <div style="background:#f0f2f5; font-weight:700; padding:10px 15px; border-bottom:1px solid #000; color:#000; display:flex; justify-content:space-between;">
                <span>รายได้</span>
                <span>จำนวนเงิน (บาท)</span>
              </div>
              <div style="padding:15px 15px 5px 15px; display:flex; flex-direction:column; gap:8px;">
                <div style="display:flex; justify-content:space-between;"><span>เงินเดือน:</span><strong>${summ.basicSalaryEarned.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                ${summ.diligenceAllowance > 0 ? `<div style="display:flex; justify-content:space-between;"><span>เบี้ยขยัน:</span><strong>${summ.diligenceAllowance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${(summ.otherIncomeItems && summ.otherIncomeItems.length > 0) ? summ.otherIncomeItems.map(item => `<div style="display:flex; justify-content:space-between;"><span>${escapeHtml(item.name)}:</span><strong>${parseFloat(item.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>`).join('') : (summ.otherIncomes > 0 ? `<div style="display:flex; justify-content:space-between;"><span>รายรับเสริมอื่นๆ:</span><strong>${summ.otherIncomes.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : '')}
                ${summ.securityDepositRefunded > 0 ? `<div style="display:flex; justify-content:space-between; color: var(--success); font-weight: 700;"><span>คืนเงินประกันสะสม:</span><strong>${summ.securityDepositRefunded.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
              </div>
            </div>
            <div style="border-top:1px dashed #ccc; padding:10px 15px; font-weight:700; background:#fafafa; display:flex; justify-content:space-between;">
              <span>รวมเงินได้</span>
              <span>${(summ.basicSalaryEarned + summ.diligenceAllowance + summ.otherIncomes + (summ.securityDepositRefunded || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <!-- Right Column (Deductions) -->
          <div style="flex:1; display:flex; flex-direction:column; justify-content:space-between;">
            <div>
              <div style="background:#f0f2f5; font-weight:700; padding:10px 15px; border-bottom:1px solid #000; color:#000; display:flex; justify-content:space-between;">
                <span>รายหัก</span>
                <span>จำนวนเงิน (บาท)</span>
              </div>
              <div style="padding:15px 15px 5px 15px; display:flex; flex-direction:column; gap:8px;">
                ${summ.lateDeduction > 0 ? `<div style="display:flex; justify-content:space-between;"><span>มาสายรายนาที:</span><strong>${summ.lateDeduction.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${summ.socialSecurityDeducted > 0 ? `<div style="display:flex; justify-content:space-between;"><span>ประกันสังคม:</span><strong>${summ.socialSecurityDeducted.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${summ.withholdingTaxDeducted > 0 ? `<div style="display:flex; justify-content:space-between;"><span>ภาษี ณ ที่จ่าย:</span><strong>${summ.withholdingTaxDeducted.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${summ.studentLoanDeducted > 0 ? `<div style="display:flex; justify-content:space-between;"><span>กยศ.:</span><strong>${summ.studentLoanDeducted.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${summ.securityDepositDeducted > 0 ? `<div style="display:flex; justify-content:space-between;"><span>เงินประกันสะสม:</span><strong>${summ.securityDepositDeducted.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
                ${(summ.otherDeductionItems && summ.otherDeductionItems.length > 0) ? summ.otherDeductionItems.map(item => `<div style="display:flex; justify-content:space-between;"><span>${escapeHtml(item.name)}:</span><strong>${parseFloat(item.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>`).join('') : (summ.otherDeductions > 0 ? `<div style="display:flex; justify-content:space-between;"><span>อื่นๆ:</span><strong>${summ.otherDeductions.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : '')}
                ${summ.totalAdvance > 0 ? `<div style="display:flex; justify-content:space-between;"><span>เบิกล่วงหน้า:</span><strong>${summ.totalAdvance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>` : ''}
              </div>
            </div>
            <div style="border-top:1px dashed #ccc; padding:10px 15px; font-weight:700; background:#fafafa; display:flex; justify-content:space-between;">
              <span>รวมยอดหัก</span>
              <span>${(summ.lateDeduction + summ.socialSecurityDeducted + summ.withholdingTaxDeducted + summ.studentLoanDeducted + summ.securityDepositDeducted + summ.otherDeductions + summ.totalAdvance).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <!-- Net Salary Summary Box -->
        <div style="border:1.5px solid #000; border-radius:6px; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; background:#f8f9fa; margin-bottom:30px;">
          <div>
            <div style="font-size:0.8rem; color:#444; font-weight:700;">ยอดเงินรับสุทธิ (ตัวอักษร)</div>
            <div style="font-size:0.92rem; font-weight:700; color:#000; margin-top:2px;">( ${thaiBahtNetText} )</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:0.8rem; color:#444; font-weight:700;">เงินได้สุทธิ</div>
            <strong style="color:#000; font-size:1.4rem;">${summ.netSalary.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</strong>
          </div>
        </div>

        <!-- Signature/Verification Footer -->
        <div style="display:flex; justify-content:space-around; align-items:flex-start; text-align:center; font-size:0.78rem; margin-top:110px;">
          
          <!-- Employee Signature -->
          <div style="width:240px; display:flex; flex-direction:column; align-items:center;">
            <div style="width:100%; border-bottom:1px solid #000; margin-top:100px; margin-bottom:10px;"></div>
            <p style="margin:0; font-weight:700; color:#000; white-space:nowrap;">( ${escapeHtml(emp.realName)} )</p>
            <p style="margin:5px 0 0 0; color:#333;">ลายมือชื่อพนักงานผู้รับเงิน</p>
            <p style="margin:2px 0 0 0; color:#666;">วันที่ <span id="payslip-printed-sign-date-emp">${new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}</span></p>
          </div>
          
          <!-- Authorized Signature -->
          <div style="width:240px; display:flex; flex-direction:column; align-items:center; position:relative;">
            <div style="width:100%; border-bottom:1px solid #000; margin-top:100px; margin-bottom:10px;"></div>
            <p style="margin:0; font-weight:700; color:#000; white-space:nowrap;">( ${escapeHtml(state.selectedPayor)} )</p>
            <p style="margin:5px 0 0 0; color:#333;">ผู้มีอำนาจลงนาม</p>
            <p style="margin:2px 0 0 0; color:#666;">${escapeHtml(comp.name)}</p>
            <p style="margin:2px 0 0 0; color:#666;">วันที่ <span id="payslip-printed-sign-date-auth">${new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}</span></p>
          </div>
        </div>
      </div>
      <div class="modal-footer" style="display:flex; justify-content:space-between; align-items:center;">
        <div style="display:flex; align-items:center; gap:10px;">
          <label style="font-size:0.9rem; font-weight:600;">วันที่พิมพ์ลงสลิป:</label>
          <input type="date" id="payslip-print-date" class="form-control" style="width:140px; font-size:0.9rem; padding:4px 8px;" value="${new Date().toISOString().slice(0, 10)}" onchange="const d = this.value ? new Date(this.value).toLocaleDateString('th-TH', {year: 'numeric', month: 'short', day: 'numeric'}) : '........ / ........ / ........'; document.getElementById('payslip-printed-sign-date-emp').innerText = d; document.getElementById('payslip-printed-sign-date-auth').innerText = d;">
        </div>
        <div>
          <button class="btn btn-secondary" onclick="document.getElementById('payslip-modal-overlay').classList.remove('active')">ปิดหน้าต่าง</button>
          <button class="btn btn-primary" onclick="printPayslipDirectly()">🖨️ พิมพ์สลิปเงินเดือนใบนี้</button>
        </div>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');
};

async function printPayslipDirectly() {
  if (state.activePayslipEmployeeId) {
    const ts = await getTimesheet(state.activePayslipEmployeeId, state.selectedMonth);
    if (!ts.isLocked) {
      alert("⚠️ ไม่สามารถพิมพ์สลิปได้ เนื่องจากข้อมูลเงินเดือนของพนักงานท่านนี้ยังไม่ได้ถูกล็อค!");
      return;
    }
  }
  const content = document.getElementById('printable-payslip-area').innerHTML;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentWindow.document;

  iframeDoc.open();
  iframeDoc.write(`
    <html>
      <head>
        <title>สลิปเงินเดือน</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&display=swap');
          body { 
            font-family: 'Sarabun', sans-serif; 
            margin: 0; 
            padding: 20px;
            background: #fff;
          }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `);
  iframeDoc.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 500);
};

// Report Tab 2: Social Security Summary & Prevents duplicates (Requirement 13)
async function renderSocialSecurityMonthlyReport(container) {
  container.innerHTML = `<div class="glass-card"><h4>กำลังสรุปยอดข้อมูลส่งหลักฐานประกันสังคมรายงวด...</h4></div>`;

  let rowsHTML = '';
  let companySSContributionTotal = 0;
  let employeeSSTotal = 0;
  let employerSSTotal = 0;

  // คำนวณเดือนก่อนหน้าเพื่อเป็น targetMonth (งวดที่เกิดยอดหัก)
  const [sy, sm] = state.selectedMonth.split('-');
  const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
  const targetYear = targetDate.getFullYear();
  const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
  const targetMonth = `${targetYear}-${targetMonthStr}`;

  const thaiMonthsArr = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  const displayTargetMonth = `${thaiMonthsArr[parseInt(targetMonthStr) - 1]} ${targetYear}`;
  const [sYear, sMonth] = state.selectedMonth.split('-');
  const displaySelectedMonth = `${thaiMonthsArr[parseInt(sMonth) - 1]} ${sYear}`;

  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, targetMonth));

  // Monthly SS Billing Period format
  const ssMonthPeriod = `<span style="color:var(--primary-dark); font-weight:bold;">${displayTargetMonth}</span>`;

  const currentMonthStr = new Date().toISOString().slice(0, 7);
  let ssEmpCount = 0;

  const visibleSSEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const ssTimesheets = await Promise.all(visibleSSEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, targetMonth)
  })));

  for (const { emp, ts } of ssTimesheets) {
    if (ts.isPhantom && targetMonth !== currentMonthStr) continue;
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

    const ssDeduction = summ.socialSecurityDeducted;
    const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

    const isSSEnabledInProfile = dedSettings.socialSecurity.enabled;
    const isSSOverriddenToZero = ts.overrides && parseFloat(ts.overrides.socialSecurityRate) === 0;

    // Filter out if SS is 0 AND (it's not enabled in profile OR it was explicitly overridden to 0)
    if (ssDeduction === 0 && (!isSSEnabledInProfile || isSSOverriddenToZero)) continue;

    ssEmpCount++;

    // Employer contributes equal amount
    const employerSSPart = ssDeduction;
    const totalSSAmount = ssDeduction + employerSSPart;

    companySSContributionTotal += totalSSAmount;
    employeeSSTotal += ssDeduction;
    employerSSTotal += employerSSPart;

    // Check payment history to avoid double payment confusion
    const isPaid = ts.ssPaymentStatus === 'paid';
    const paidDate = ts.ssPaymentDate || '-';

    const payStatusButton = isPaid ?
      `<div style="display:flex; align-items:center; gap:5px;">
         <span class="badge badge-success">✅ นำส่งแล้วเมื่อ: ${paidDate}</span>
         ${window.checkPermission('edit_timesheet') ? `<button class="btn btn-outline-danger btn-sm" style="padding:2px 6px; font-size:0.75rem; border:none; background:transparent;" onclick="triggerUnmarkSSPaid('${escapeJsParam(emp.id)}')" title="ยกเลิกการบันทึก">❌</button>` : ''}
       </div>` :
      (window.checkPermission('edit_timesheet') ? `
        <button class="btn btn-secondary btn-sm" onclick="triggerMarkSSPaid('${escapeJsParam(emp.id)}')">💾 ลงบันทึกชำระเงินแล้ว</button>
      ` : '🔒 รอนำส่ง');

    const ssBaseSalary = summ.basicSalaryEarned - summ.lateDeduction;

    rowsHTML += `
      <tr>
        <td><strong>${escapeHtml(emp.realName)}</strong></td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td>${ssMonthPeriod}</td>
        <td>${ssBaseSalary.toLocaleString()} บาท</td>
        <td>${ssDeduction.toLocaleString()} บาท</td>
        <td>${employerSSPart.toLocaleString()} บาท</td>
        <td><strong>${totalSSAmount.toLocaleString()} บาท</strong></td>
        <td>${payStatusButton}</td>
      </tr>
    `;
  }

  if (ssEmpCount === 0) {
    container.innerHTML = `
      <div class="glass-card text-center">
        <h4 style="color:var(--text-sub);">ไม่พบพนักงานในระบบที่ได้รับสิทธิ์ประกันสังคมในรอบเดือน ${displayTargetMonth}</h4>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="glass-card" style="display:flex; flex-direction:column; gap:20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:15px;">
        <div style="display:flex; flex-direction:column; gap:8px;">
          <h4 style="font-weight:700; color:var(--primary-dark); margin:0;">รายละเอียดข้อมูลนำส่งประกันสังคม</h4>
          <span style="font-size:0.95rem; color:var(--text-main);">ยอดสรุปสำหรับรอบเดือน (งวดที่โดนหัก): <span class="badge" style="background:#ffebe9; color:#cf222e; font-size:1rem; padding:4px 10px; border: 1px solid #ff8182;">${displayTargetMonth}</span></span>
          <span style="font-size:0.9rem; color:#dc3545; font-weight:600;">* กำหนดส่งภายในวันที่ 15 <strong>${displaySelectedMonth}</strong></span>
          <div style="margin-top:5px; display:flex; gap:10px; flex-wrap:wrap;">
            ${window.checkPermission('edit_timesheet') ? `
              <button class="btn btn-primary btn-sm" onclick="triggerMarkAllSSPaid()">✅ ลงบันทึกให้ทุกคน</button>
              <button class="btn btn-outline-danger btn-sm" onclick="triggerUnmarkAllSSPaid()">❌ ยกเลิกทั้งหมด</button>
            ` : ''}
            ${window.checkPermission('export_sso_report') ? `
              <button class="btn btn-secondary btn-sm" onclick="printSSOReport()">🖨️ พิมพ์รายงาน (สปส.1-10)</button>
            ` : ''}
          </div>
        </div>
        <div style="text-align:right; display:flex; flex-direction:column; gap:4px;">
          <div style="font-size:1.1rem; color:var(--primary-dark); margin-bottom:4px;">🏥 รวมยอดสมทบส่ง สปส. ของงวดนี้: <strong>${companySSContributionTotal.toLocaleString()} บาท</strong></div>
          <div style="font-size:0.95rem; color:var(--text-sub);">รวมสมทบพนักงาน: <strong>${employeeSSTotal.toLocaleString()} บาท</strong></div>
          <div style="font-size:0.95rem; color:var(--text-sub);">รวมสมทบนายจ้าง: <strong>${employerSSTotal.toLocaleString()} บาท</strong></div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="payroll-table">
          <thead>
            <tr>
              <th>ลูกน้องผู้ประกันตน</th>
              <th>เลขบัตรประชาชน</th>
              <th>งวดนำส่ง</th>
              <th>ฐานที่ใช้คำนวณ</th>
              <th>หักพนักงาน (5%)</th>
              <th>สมทบนายจ้าง (5%)</th>
              <th>รวมทั้งสิ้น</th>
              <th>บันทึกสถานะการโอนจ่ายเงิน</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

async function triggerMarkSSPaid(empId) {
  if (!confirm("คุณยืนยันลงประวัติการจ่ายและนำส่งเงินประกันสังคมงวดนี้แล้วจริงหรือไม่? เพื่อป้องกันการนำส่งจ่ายซ้ำซ้อน")) return;
  try {
    // คำนวณ targetMonth (เดือนก่อนหน้า)
    const [sy, sm] = state.selectedMonth.split('-');
    const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
    const targetYear = targetDate.getFullYear();
    const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
    const targetMonth = `${targetYear}-${targetMonthStr}`;

    const ts = await getTimesheet(empId, targetMonth);
    ts.ssPaymentStatus = 'paid';

    // ใช้วันที่ปัจจุบันในการบันทึกว่าจ่ายตอนไหน
    const today = new Date();
    ts.ssPaymentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    await saveTimesheet(empId, targetMonth, ts);
    alert("บันทึกประวัตินำส่งสำเร็จ!");
    switchReportTab('ss'); // Reload
  } catch (err) {
    console.error(err);
  }
};

async function triggerUnmarkSSPaid(empId) {
  if (!confirm("คุณต้องการยกเลิกสถานะ 'นำส่งแล้ว' ของพนักงานรายนี้ใช่หรือไม่?")) return;
  try {
    const [sy, sm] = state.selectedMonth.split('-');
    const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
    const targetYear = targetDate.getFullYear();
    const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
    const targetMonth = `${targetYear}-${targetMonthStr}`;

    const ts = await getTimesheet(empId, targetMonth);
    ts.ssPaymentStatus = null;
    ts.ssPaymentDate = null;

    await saveTimesheet(empId, targetMonth, ts);
    switchReportTab('ss'); // Reload
  } catch (err) {
    console.error(err);
  }
};

async function triggerMarkAllSSPaid() {
  if (!confirm("คุณยืนยันที่จะ 'ลงบันทึกชำระเงิน' ประกันสังคมของงวดนี้ให้กับพนักงานทุกคนพร้อมกันใช่หรือไม่?")) return;
  try {
    const [sy, sm] = state.selectedMonth.split('-');
    const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
    const targetYear = targetDate.getFullYear();
    const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
    const targetMonth = `${targetYear}-${targetMonthStr}`;

    const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, targetMonth));
    const today = new Date();
    const paidDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const visibleMarkEmps = activeEmps.filter(emp => window.canViewSalary(emp));
    const markPromises = visibleMarkEmps.map(async (emp) => {
      const ts = await getTimesheet(emp.id, targetMonth);
      const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

      const ssDeduction = summ.socialSecurityDeducted;
      const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);
      const isSSEnabledInProfile = dedSettings.socialSecurity.enabled;
      const isSSOverriddenToZero = ts.overrides && parseFloat(ts.overrides.socialSecurityRate) === 0;

      if (ssDeduction === 0 && (!isSSEnabledInProfile || isSSOverriddenToZero)) return false;

      if (ts.ssPaymentStatus !== 'paid') {
        ts.ssPaymentStatus = 'paid';
        ts.ssPaymentDate = paidDate;
        await saveTimesheet(emp.id, targetMonth, ts);
        return true;
      }
      return false;
    });

    const markResults = await Promise.all(markPromises);
    const updatedCount = markResults.filter(Boolean).length;

    alert(`ลงบันทึกชำระเงินสำเร็จทั้งหมด ${updatedCount} รายการ!`);
    switchReportTab('ss');
  } catch (err) {
    console.error(err);
  }
};

async function triggerUnmarkAllSSPaid() {
  if (!confirm("คุณต้องการ 'ยกเลิก' สถานะนำส่งแล้ว ของพนักงานทุกคนในงวดนี้ใช่หรือไม่?")) return;
  try {
    const [sy, sm] = state.selectedMonth.split('-');
    const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
    const targetYear = targetDate.getFullYear();
    const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
    const targetMonth = `${targetYear}-${targetMonthStr}`;

    const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, targetMonth));

    const visibleUnmarkEmps = activeEmps.filter(emp => window.canViewSalary(emp));
    const unmarkPromises = visibleUnmarkEmps.map(async (emp) => {
      const ts = await getTimesheet(emp.id, targetMonth);
      if (ts.ssPaymentStatus === 'paid') {
        ts.ssPaymentStatus = null;
        ts.ssPaymentDate = null;
        await saveTimesheet(emp.id, targetMonth, ts);
        return true;
      }
      return false;
    });

    const unmarkResults = await Promise.all(unmarkPromises);
    const updatedCount = unmarkResults.filter(Boolean).length;

    alert(`ยกเลิกสถานะสำเร็จทั้งหมด ${updatedCount} รายการ!`);
    switchReportTab('ss');
  } catch (err) {
    console.error(err);
  }
};

// Report Tab 3: Withholding Tax Report (Requirement 14)
async function renderWithholdingTaxReport(container) {
  container.innerHTML = `<div class="glass-card"><h4>กำลังวิเคราะห์สรุปรายงานภาษี หัก ณ ที่จ่าย...</h4></div>`;

  let rowsHTML = '';
  let companyTaxTotal = 0;

  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));

  const currentMonthStr = new Date().toISOString().slice(0, 7);
  let taxEmpCount = 0;

  const visibleTaxEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const taxTimesheets = await Promise.all(visibleTaxEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, state.selectedMonth)
  })));

  for (const { emp, ts } of taxTimesheets) {
    if (ts.isPhantom && state.selectedMonth !== currentMonthStr) continue;
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
    const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

    const taxAmt = summ.withholdingTaxDeducted;
    const taxableIncome = summ.taxableIncome !== undefined
      ? summ.taxableIncome
      : Math.max(0, (emp.type === 'freelance')
          ? (summ.basicSalaryEarned + (summ.diligenceAllowance || 0) + (summ.otherIncomes || 0))
          : (summ.basicSalaryEarned - (summ.lateDeduction || 0) + (summ.diligenceAllowance || 0) + (summ.otherIncomes || 0)));

    const isTaxEnabledInProfile = dedSettings.withholdingTax.enabled;
    const isTaxOverriddenToZero = ts.overrides && parseFloat(ts.overrides.withholdingTaxRate) === 0;

    // Filter out if Tax is 0 AND (it's not enabled in profile OR it was explicitly overridden to 0)
    if (taxAmt === 0 && (!isTaxEnabledInProfile || isTaxOverriddenToZero)) continue;

    taxEmpCount++;
    companyTaxTotal += taxAmt;

    const typeText = emp.type === 'salary' ? 'เงินเดือนประจำ' : (emp.type === 'daily' ? 'รายวัน' : 'ฟรีแลนซ์');
    const displayRate = (ts.overrides && ts.overrides.withholdingTaxRate !== undefined) ? ts.overrides.withholdingTaxRate : dedSettings.withholdingTax.rate;

    rowsHTML += `
      <tr>
        <td><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td>${typeText}</td>
        <td>${taxableIncome.toLocaleString()} บาท</td>
        <td>${displayRate}%</td>
        <td><strong>${taxAmt.toLocaleString()} บาท</strong></td>
      </tr>
    `;
  }

  if (taxEmpCount === 0) {
    container.innerHTML = `
      <div class="glass-card text-center">
        <h4 style="color:var(--text-sub);">ไม่พบลูกน้องในระบบที่มีการหัก ณ ที่จ่ายงวดนี้</h4>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="glass-card" style="display:flex; flex-direction:column; gap:20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:15px;">
        <h4 style="font-weight:700; color:var(--primary-dark);">สรุปภาษีหัก ณ ที่จ่าย (ตามมาตรา 40) ประจำงวด</h4>
        <div style="display:flex; align-items:center; gap:15px;">
          <div>ภาษีนำส่งรวมงวดนี้: <strong>${companyTaxTotal.toLocaleString()} บาท</strong></div>
          ${window.checkPermission('export_tax_report') ? `<button class="btn btn-secondary btn-sm" onclick="printTaxReport()">🖨️ พิมพ์รายงาน (ภ.ง.ด.1)</button>` : ''}
        </div>
      </div>

      <div class="table-responsive">
        <table class="payroll-table">
          <thead>
            <tr>
              <th>ชื่อ - นามสกุล (ผู้รับเงิน)</th>
              <th>เลขบัตรประชาชน</th>
              <th>ประเภท</th>
              <th>ยอดเงินได้ประเมินรวม</th>
              <th>อัตราหักภาษี</th>
              <th>ยอดภาษี หัก ณ ที่จ่ายสุทธิ</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

// Report Tab 4: KT.26 G Annual Report calculations (Requirement 15, 24)
async function renderKT26AnnualReport(container) {
  const selectedYear = state.selectedMonth.split('-')[0];
  const targetYear = parseInt(selectedYear) - 1; // Always calculate for the PREVIOUS year

  container.innerHTML = `<div class="glass-card"><h4>กำลังรวบรวมค่าจ้างสะสม กท.26 ก ตลอดปีปฏิทิน ${targetYear + 543}...</h4></div>`;

  let totalAnnualWagesAll = 0;
  let ssiEmployeesListHTML = '';
  let ssEmployeesCount = 0;

  // Let's filter employees who have social security at any point (i.e. not intern, not freelance)
  const ssEmployees = state.employees.filter(e => {
    const dedSettings = getEmployeeDeductionSettings(e, state.activeCompanySettings);
    return dedSettings.socialSecurity.enabled;
  });

  if (ssEmployees.length === 0) {
    container.innerHTML = `
      <div class="glass-card text-center">
        <h4 style="color:var(--text-sub);">ไม่พบข้อมูลพนักงานที่ผู้ประกันตนในระบบของบริษัทนี้</h4>
      </div>
    `;
    return;
  }

  // Filter eligible SS employees who started in or before the targetYear
  const eligibleSSEmployees = ssEmployees.filter(emp => {
    if (!window.canViewSalary(emp)) return false;
    const startYear = emp.startDate ? parseInt(emp.startDate.split('-')[0]) : 0;
    return startYear <= targetYear;
  });

  // Fetch all 12 months in parallel for all eligible employees
  const ssEmpAnnualData = await Promise.all(eligibleSSEmployees.map(async (emp) => {
    const monthPromises = Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const monthKey = `${targetYear}-${String(m).padStart(2, '0')}`;
      return getTimesheet(emp.id, monthKey).then(ts => ({ m, monthKey, ts }));
    });
    const months = await Promise.all(monthPromises);
    return { emp, months };
  }));

  for (const { emp, months } of ssEmpAnnualData) {
    let employeeYearlyWages = 0;
    let monthsCheckedCount = 0;

    let monthlyBreakdownHTML = '<ul style="list-style:none; margin:0; padding:0; text-align: left; font-size:0.85rem; color:var(--text-sub);">';

    for (const { m, monthKey, ts } of months) {
      if (ts && ts.summary && ts.summary.basicSalaryEarned > 0) {
        // By default, exclude diligence allowance from social security wages (Requirement 7)
        let wageInMonth = ts.summary.basicSalaryEarned - ts.summary.lateDeduction;

        // Reverse-calculate to check if historical data incorrectly included diligence allowance
        const diligence = ts.summary.diligenceAllowance || 0;
        const ssDeducted = ts.summary.socialSecurityDeducted || 0;

        if (diligence > 0 && ssDeducted > 0) {
          const base2 = wageInMonth + diligence;
          const rate1 = ssDeducted / wageInMonth;
          const rate2 = ssDeducted / base2;

          const isCleanRate = (rate) => {
            const pct = rate * 100;
            return Math.abs(pct - Math.round(pct)) < 0.01;
          };

          const maxBase = targetYear <= 2025 ? 15000 : 17500;

          let matchedBase2 = false;
          if (isCleanRate(rate2) && !isCleanRate(rate1)) {
            matchedBase2 = true;
          } else {
            const possibleRates = [0.05, 0.04, 0.03, 0.02, 0.01];
            for (const r of possibleRates) {
              const expectedSS2 = Math.round(Math.min(maxBase, Math.max(1650, base2)) * r);
              const expectedSS1 = Math.round(Math.min(maxBase, Math.max(1650, wageInMonth)) * r);
              if (ssDeducted === expectedSS2 && ssDeducted !== expectedSS1) {
                matchedBase2 = true;
                break;
              }
            }
          }

          if (matchedBase2) {
            wageInMonth = base2;
          }
        }

        employeeYearlyWages += wageInMonth;
        monthsCheckedCount++;
        monthlyBreakdownHTML += `<li style="padding: 2px 0; border-bottom: 1px solid rgba(0,0,0,0.05); display: flex; justify-content: space-between;"><span>เดือน ${m}:</span> <strong>${wageInMonth.toLocaleString()} บาท</strong></li>`;
      } else {
        monthlyBreakdownHTML += `<li style="padding: 2px 0; border-bottom: 1px solid rgba(0,0,0,0.05); display: flex; justify-content: space-between; color: #aaa;"><span>เดือน ${m}:</span> <span>-</span></li>`;
      }
    }
    monthlyBreakdownHTML += '</ul>';

    // Always list the employee if they were active in the targetYear, even if wages are 0.
    // This helps the user realize they need to fill out the timesheets for that year.
    ssEmployeesCount++;
    totalAnnualWagesAll += employeeYearlyWages;

    ssiEmployeesListHTML += `
      <tr>
        <td><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td>${emp.type === 'salary' ? 'รายเดือน' : 'รายวัน'}</td>
        <td>${emp.startDate}</td>
        <td>
          <div style="display:flex; flex-direction:column; gap:5px;">
            <strong>${employeeYearlyWages.toLocaleString()} บาท</strong>
            <details style="background: var(--gray-100); border-radius: 4px; padding: 4px 8px; border: 1px solid var(--gray-300);">
              <summary style="cursor: pointer; font-size: 0.8rem; color: var(--primary); font-weight: 600; outline: none; user-select: none;">ดูรายเดือน 🔽</summary>
              <div style="margin-top: 8px;">
                ${monthlyBreakdownHTML}
              </div>
            </details>
          </div>
        </td>
      </tr>
    `;
  }

  // Count active employees on Dec 31st of that year (Requirement 15)
  // i.e. status is active OR status is resigned but resigned after Dec 31st of the target year
  // ONLY count employees enrolled in Social Security AND hired before or during the target year
  const activeOnDec31Count = ssEmployees.filter(e => {
    const startYear = e.startDate ? parseInt(e.startDate.split('-')[0]) : 0;
    if (startYear > targetYear) return false;

    if (e.status === 'active') return true;
    if (e.status === 'resigned' && e.resignDate) {
      const resignYear = parseInt(e.resignDate.split('-')[0]);
      if (resignYear > targetYear) return true;
    }
    return false;
  }).length;

  container.innerHTML = `
    <div class="glass-card" style="display:flex; flex-direction:column; gap:20px;">
      <div style="border-bottom:1px solid var(--gray-300); padding-bottom:15px; display:flex; justify-content:space-between; align-items:flex-start;">
        <div>
          <h4 style="font-weight:700; color:var(--primary-dark); font-size:1.2rem;">แบบรายงานผลค่าจ้างสะสมประจำปี (กท.26 ก.) ข้อมูลปี ${targetYear + 543}</h4>
          <p style="color:var(--text-sub); font-size:0.85rem; margin-top:5px;">แบบสรุปสถิตินำส่ง สปส. เพื่อรายงานค่าจ้างประจำปีปฏิทิน พ.ศ. ${targetYear + 543}</p>
        </div>
        ${window.checkPermission('export_kt26k_report') ? `<button class="btn btn-secondary" onclick="printKT26kReport()">🖨️ พิมพ์รายงานแบบ กท.26ก</button>` : ''}
      </div>

      <div class="dashboard-grid">
        <div style="background:var(--gray-200); padding:15px; border-radius:var(--border-radius-sm);">
          <span style="font-size:0.85rem; color:var(--text-sub);">👥 พนักงานที่มีสิทธิ์ส่ง สปส. สะสมในปีนี้:</span>
          <h3 style="font-weight:700; color:var(--primary-dark); margin-top:5px;">${ssEmployeesCount} คน</h3>
        </div>
        <div style="background:var(--gray-200); padding:15px; border-radius:var(--border-radius-sm);">
          <span style="font-size:0.85rem; color:var(--text-sub);">💵 ยอดรวมค่าจ้างสะสมทั้งปี (ทุกผู้ประกันตน):</span>
          <h3 style="font-weight:700; color:var(--success); margin-top:5px;">${totalAnnualWagesAll.toLocaleString()} บาท</h3>
        </div>
        <div style="background:var(--gray-200); padding:15px; border-radius:var(--border-radius-sm);">
          <span style="font-size:0.85rem; color:var(--text-sub);">📊 พนักงานสะสม ณ วันที่ 31 ธันวาคม:</span>
          <h3 style="font-weight:700; color:var(--info); margin-top:5px;">${activeOnDec31Count} คน</h3>
        </div>
      </div>

      <h5 style="font-weight:700; color:var(--primary-dark); margin-top:10px;">รายชื่อพนักงานที่มีสิทธิ์ส่ง สปส.</h5>
      <div class="table-responsive">
        <table class="payroll-table">
          <thead>
            <tr>
              <th>ชื่อ - นามสกุล (ลูกน้อง)</th>
              <th>เลขบัตรประจำตัวประชาชน</th>
              <th>ประเภทค่าจ้าง</th>
              <th>วันที่เริ่มงาน</th>
              <th>ยอดเงินค่าจ้างทั้งปี</th>
            </tr>
          </thead>
          <tbody>
            ${ssiEmployeesListHTML}
          </tbody>
        </table>
      </div>
      
      <!-- Support entering historical months (Requirement 24) -->
      <div style="background:rgba(255, 124, 96, 0.05); border:1.5px dashed var(--primary); padding:15px; border-radius:var(--border-radius-md); margin-top:10px;">
        <h5 style="font-weight:700; color:var(--primary-dark);">📝 คำแนะนำในการใส่ข้อมูลเดือนย้อนหลัง</h5>
        <p style="font-size:0.82rem; color:var(--text-sub); margin-top:5px;">แอปพลิเคชันรองรับการกรอกข้อมูลของเดือนเก่าย้อนหลังอย่างสมบูรณ์เพื่อประโยชน์ในการสรุป กท.26 ก. คุณสามารถปรับงวดบัญชีด้านบนสุดให้เป็นเดือนเก่าของปีนั้นๆ แล้วเข้าไปบันทึกเวลาทำงานและเงินเดือนได้เลย ข้อมูลเก่าเหล่านั้นจะมารวบรวมที่นี่โดยอัตโนมัติ</p>
      </div>
    </div>
  `;
}



// ====== Print Functions ======
function createPrintIframe(title, htmlContent) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentWindow.document;

  iframeDoc.open();
  iframeDoc.write(`
    <html>
      <head>
        <title>${title}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&display=swap');
          body { 
            font-family: 'Sarabun', sans-serif; 
            margin: 0; 
            padding: 30px;
            background: #fff;
            color: #000;
          }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 0.95rem; }
          th, td { border: 1px solid #333; padding: 10px; text-align: center; }
          th { background-color: #f8f9fa; font-weight: bold; }
          .text-left { text-align: left !important; }
          .text-right { text-align: right !important; }
          h2, h3, h4 { text-align: center; margin: 5px 0; }
          .header-info { text-align: center; margin-bottom: 25px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `);
  iframeDoc.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1500);
  }, 1000); // Give fonts time to load
}

async function printTransferSummaryReport() {
  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  let totalTransferAmt = 0;
  let rows = '';

  const visiblePrintTransEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const printTransTimesheets = await Promise.all(visiblePrintTransEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, state.selectedMonth)
  })));

  for (const { emp, ts } of printTransTimesheets) {
    if (ts.isPhantom && state.selectedMonth !== currentMonthStr) continue;
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

    totalTransferAmt += summ.netSalary;

    const printBankDisplay = emp.bankAccount ? `${emp.bankName ? `<strong>${escapeHtml(emp.bankName)}</strong><br>` : ''}${escapeHtml(emp.bankAccount)}` : 'รับเงินสด';

    rows += `
      <tr>
        <td class="text-left">${escapeHtml(emp.realName)} (${escapeHtml(emp.nickname)})</td>
        <td>${printBankDisplay}</td>
        <td class="text-right">${summ.netSalary.toLocaleString()}</td>
      </tr>
    `;
  }

  const html = `
    <div class="header-info">
      <h2>รายงานสรุปการสั่งจ่ายเงินโอนธนาคาร</h2>
      <h3>บริษัท ${escapeHtml(state.activeCompanySettings.name)}</h3>
      <p>ประจำเดือน ${state.selectedMonth}</p>
    </div>
    <table>
      <thead>
        <tr>
          <th style="width: 40%">ชื่อ - นามสกุล</th>
          <th style="width: 30%">เลขที่บัญชีธนาคาร</th>
          <th style="width: 30%">ยอดรับสุทธิ (บาท)</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="3">ไม่มีข้อมูล</td></tr>'}
      </tbody>
      <tfoot>
        <tr>
          <th colspan="2" class="text-right">ยอดรวมโอนสุทธิทั้งหมด</th>
          <th class="text-right">${totalTransferAmt.toLocaleString()}</th>
        </tr>
      </tfoot>
    </table>
  `;
  createPrintIframe('Transfer_Report_' + state.selectedMonth, html);
};

async function printSSOReport() {
  const [sy, sm] = state.selectedMonth.split('-');
  const targetDate = new Date(parseInt(sy), parseInt(sm) - 1 - 1, 1);
  const targetYear = targetDate.getFullYear();
  const targetMonthStr = String(targetDate.getMonth() + 1).padStart(2, '0');
  const targetMonth = `${targetYear}-${targetMonthStr}`;
  const thaiMonthsArr = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  const displayTargetMonth = `${thaiMonthsArr[parseInt(targetMonthStr) - 1]} ${targetYear}`;

  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, targetMonth));

  let companySSContributionTotal = 0;
  let employeeSSTotal = 0;
  let employerSSTotal = 0;
  let rows = '';
  let idx = 1;

  const visiblePrintSSOEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const printSSOTimesheets = await Promise.all(visiblePrintSSOEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, targetMonth)
  })));

  for (const { emp, ts } of printSSOTimesheets) {
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
    const ssDeduction = summ.socialSecurityDeducted;
    const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);
    const isSSEnabledInProfile = dedSettings.socialSecurity.enabled;
    const isSSOverriddenToZero = ts.overrides && parseFloat(ts.overrides.socialSecurityRate) === 0;

    if (ssDeduction === 0 && (!isSSEnabledInProfile || isSSOverriddenToZero)) continue;

    const employerSSPart = ssDeduction;
    const totalSSAmount = ssDeduction + employerSSPart;
    const ssBaseSalary = summ.basicSalaryEarned - summ.lateDeduction;

    companySSContributionTotal += totalSSAmount;
    employeeSSTotal += ssDeduction;
    employerSSTotal += employerSSPart;

    rows += `
      <tr>
        <td>${idx++}</td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td class="text-left">${escapeHtml(emp.realName)}</td>
        <td class="text-right">${ssBaseSalary.toLocaleString()}</td>
        <td class="text-right">${ssDeduction.toLocaleString()}</td>
        <td class="text-right">${employerSSPart.toLocaleString()}</td>
        <td class="text-right">${totalSSAmount.toLocaleString()}</td>
      </tr>
    `;
  }

  const html = `
    <div class="header-info">
      <h2>แบบรายละเอียดข้อมูลนำส่งเงินสมทบ (สปส.1-10)</h2>
      <h3>บริษัท ${escapeHtml(state.activeCompanySettings.name)}</h3>
      <p>งวดเดือน ${displayTargetMonth}</p>
    </div>
    <table>
      <thead>
        <tr>
          <th>ลำดับ</th>
          <th>เลขบัตรประชาชน</th>
          <th>ชื่อผู้ประกันตน</th>
          <th>ค่าจ้าง (บาท)</th>
          <th>ผู้ประกันตนสมทบ</th>
          <th>นายจ้างสมทบ</th>
          <th>รวมเงินสมทบ</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="7">ไม่มีข้อมูล</td></tr>'}
      </tbody>
      <tfoot>
        <tr>
          <th colspan="3" class="text-right">ยอดรวมทั้งหมด</th>
          <th class="text-right"></th>
          <th class="text-right">${employeeSSTotal.toLocaleString()}</th>
          <th class="text-right">${employerSSTotal.toLocaleString()}</th>
          <th class="text-right">${companySSContributionTotal.toLocaleString()}</th>
        </tr>
      </tfoot>
    </table>
  `;
  createPrintIframe('SSO_Report_' + targetMonth, html);
};

async function printTaxReport() {
  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  let companyTaxTotal = 0;
  let rows = '';
  let idx = 1;

  const visiblePrintTaxEmps = activeEmps.filter(emp => window.canViewSalary(emp));
  const printTaxTimesheets = await Promise.all(visiblePrintTaxEmps.map(async (emp) => ({
    emp,
    ts: await getTimesheet(emp.id, state.selectedMonth)
  })));

  for (const { emp, ts } of printTaxTimesheets) {
    if (ts.isPhantom && state.selectedMonth !== currentMonthStr) continue;
    const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
    const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

    const taxAmt = summ.withholdingTaxDeducted;
    const taxableIncome = summ.taxableIncome !== undefined
      ? summ.taxableIncome
      : Math.max(0, (emp.type === 'freelance')
          ? (summ.basicSalaryEarned + (summ.diligenceAllowance || 0) + (summ.otherIncomes || 0))
          : (summ.basicSalaryEarned - (summ.lateDeduction || 0) + (summ.diligenceAllowance || 0) + (summ.otherIncomes || 0)));
    const isTaxEnabledInProfile = dedSettings.withholdingTax.enabled;
    const isTaxOverriddenToZero = ts.overrides && parseFloat(ts.overrides.withholdingTaxRate) === 0;

    if (taxAmt === 0 && (!isTaxEnabledInProfile || isTaxOverriddenToZero)) continue;

    companyTaxTotal += taxAmt;
    const typeText = emp.type === 'salary' ? 'รายเดือน' : (emp.type === 'daily' ? 'รายวัน' : 'ฟรีแลนซ์');
    const displayRate = (ts.overrides && ts.overrides.withholdingTaxRate !== undefined) ? ts.overrides.withholdingTaxRate : dedSettings.withholdingTax.rate;

    rows += `
      <tr>
        <td>${idx++}</td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td class="text-left">${escapeHtml(emp.realName)}</td>
        <td>${typeText}</td>
        <td class="text-right">${taxableIncome.toLocaleString()}</td>
        <td class="text-right">${displayRate}%</td>
        <td class="text-right">${taxAmt.toLocaleString()}</td>
      </tr>
    `;
  }

  const html = `
    <div class="header-info">
      <h2>รายงานภาษีหัก ณ ที่จ่าย (ภ.ง.ด.1)</h2>
      <h3>บริษัท ${escapeHtml(state.activeCompanySettings.name)}</h3>
      <p>ประจำเดือน ${state.selectedMonth}</p>
    </div>
    <table>
      <thead>
        <tr>
          <th>ลำดับ</th>
          <th>เลขประจำตัวประชาชน/ผู้เสียภาษี</th>
          <th>ชื่อ - นามสกุล</th>
          <th>ประเภท</th>
          <th>จำนวนเงินที่จ่าย (บาท)</th>
          <th>อัตราหัก (%)</th>
          <th>ภาษีที่หักและนำส่ง (บาท)</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="7">ไม่มีข้อมูล</td></tr>'}
      </tbody>
      <tfoot>
        <tr>
          <th colspan="6" class="text-right">รวมภาษีนำส่งทั้งสิ้น</th>
          <th class="text-right">${companyTaxTotal.toLocaleString()}</th>
        </tr>
      </tfoot>
    </table>
  `;
  createPrintIframe('Tax_Report_' + state.selectedMonth, html);
};

async function printKT26kReport() {
  const selectedYear = state.selectedMonth.split('-')[0];
  const targetYear = parseInt(selectedYear) - 1;

  let totalAnnualWagesAll = 0;
  let rows = '';
  let idx = 1;

  const ssEmployees = state.employees.filter(e => {
    const dedSettings = getEmployeeDeductionSettings(e, state.activeCompanySettings);
    return dedSettings.socialSecurity.enabled;
  });

  const eligiblePrintKT26Employees = ssEmployees.filter(emp => {
    if (!window.canViewSalary(emp)) return false;
    const startYear = emp.startDate ? parseInt(emp.startDate.split('-')[0]) : 0;
    return startYear <= targetYear;
  });

  const printKT26kData = await Promise.all(eligiblePrintKT26Employees.map(async (emp) => {
    const monthPromises = Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const monthKey = `${targetYear}-${String(m).padStart(2, '0')}`;
      return getTimesheet(emp.id, monthKey).then(ts => ({ m, monthKey, ts }));
    });
    const months = await Promise.all(monthPromises);
    return { emp, months };
  }));

  for (const { emp, months } of printKT26kData) {
    let employeeYearlyWages = 0;
    for (const { m, monthKey, ts } of months) {
      if (ts && ts.summary && ts.summary.basicSalaryEarned > 0) {
        let wageInMonth = ts.summary.basicSalaryEarned - ts.summary.lateDeduction;
        const diligence = ts.summary.diligenceAllowance || 0;
        const ssDeducted = ts.summary.socialSecurityDeducted || 0;

        if (diligence > 0 && ssDeducted > 0) {
          const base2 = wageInMonth + diligence;
          const rate1 = ssDeducted / wageInMonth;
          const rate2 = ssDeducted / base2;
          const isCleanRate = (rate) => {
            const pct = rate * 100;
            return Math.abs(pct - Math.round(pct)) < 0.01;
          };
          const maxBase = targetYear <= 2025 ? 15000 : 17500;
          let matchedBase2 = false;
          if (isCleanRate(rate2) && base2 <= maxBase) matchedBase2 = true;
          else if (isCleanRate(rate2) && base2 > maxBase && ssDeducted === (maxBase * rate2)) matchedBase2 = true;

          if (matchedBase2) wageInMonth = base2;
        }
        employeeYearlyWages += wageInMonth;
      }
    }

    totalAnnualWagesAll += employeeYearlyWages;

    rows += `
      <tr>
        <td>${idx++}</td>
        <td class="text-left">${escapeHtml(emp.realName)}</td>
        <td>${escapeHtml(emp.citizenId)}</td>
        <td>${emp.type === 'salary' ? 'รายเดือน' : 'รายวัน'}</td>
        <td class="text-right">${employeeYearlyWages.toLocaleString()}</td>
      </tr>
    `;
  }

  const activeOnDec31Count = ssEmployees.filter(e => {
    const startYear = e.startDate ? parseInt(e.startDate.split('-')[0]) : 0;
    if (startYear > targetYear) return false;
    if (e.status === 'active') return true;
    if (e.status === 'resigned' && e.resignDate) {
      const resignYear = parseInt(e.resignDate.split('-')[0]);
      if (resignYear > targetYear) return true;
    }
    return false;
  }).length;

  const html = `
    <div class="header-info">
      <h2>แบบรายงานผลค่าจ้างสะสมประจำปี (กท.26 ก.)</h2>
      <h3>บริษัท ${escapeHtml(state.activeCompanySettings.name)}</h3>
      <p>ข้อมูลสำหรับปี พ.ศ. ${targetYear + 543}</p>
    </div>
    <table>
      <thead>
        <tr>
          <th>ลำดับ</th>
          <th>ชื่อ - นามสกุล ผู้ประกันตน</th>
          <th>เลขบัตรประชาชน</th>
          <th>ประเภทค่าจ้าง</th>
          <th>รวมค่าจ้างสะสมทั้งปี (บาท)</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5">ไม่มีข้อมูล</td></tr>'}
      </tbody>
      <tfoot>
        <tr>
          <th colspan="4" class="text-right">รวมค่าจ้างสะสมทั้งหมด</th>
          <th class="text-right">${totalAnnualWagesAll.toLocaleString()}</th>
        </tr>
      </tfoot>
    </table>
    <div style="margin-top: 20px; font-weight: bold;">
      * จำนวนพนักงานที่มีสิทธิ์ส่ง สปส. สะสม ณ วันที่ 31 ธันวาคม: ${activeOnDec31Count} คน
    </div>
  `;
  createPrintIframe('KT26k_Report_' + targetYear, html);
};


// Bind all report functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.renderReportsPage = renderReportsPage;
  window.switchReportTab = switchReportTab;
  window.renderBankSummaryReport = renderBankSummaryReport;
  window.cleanGarbageTimesheetsTrigger = cleanGarbageTimesheetsTrigger;
  window.copyFinanceSummaryToClipboard = copyFinanceSummaryToClipboard;
  window.uploadSlipTrigger = uploadSlipTrigger;
  window.showPayslipModal = showPayslipModal;
  window.printPayslipDirectly = printPayslipDirectly;
  window.renderSocialSecurityMonthlyReport = renderSocialSecurityMonthlyReport;
  window.triggerMarkSSPaid = triggerMarkSSPaid;
  window.triggerUnmarkSSPaid = triggerUnmarkSSPaid;
  window.triggerMarkAllSSPaid = triggerMarkAllSSPaid;
  window.triggerUnmarkAllSSPaid = triggerUnmarkAllSSPaid;
  window.renderWithholdingTaxReport = renderWithholdingTaxReport;
  window.renderKT26AnnualReport = renderKT26AnnualReport;
  window.createPrintIframe = createPrintIframe;
  window.printTransferSummaryReport = printTransferSummaryReport;
  window.printSSOReport = printSSOReport;
  window.printTaxReport = printTaxReport;
  window.printKT26kReport = printKT26kReport;
}

export {
  renderReportsPage,
  switchReportTab,
  renderBankSummaryReport,
  cleanGarbageTimesheetsTrigger,
  copyFinanceSummaryToClipboard,
  uploadSlipTrigger,
  showPayslipModal,
  printPayslipDirectly,
  renderSocialSecurityMonthlyReport,
  triggerMarkSSPaid,
  triggerUnmarkSSPaid,
  triggerMarkAllSSPaid,
  triggerUnmarkAllSSPaid,
  renderWithholdingTaxReport,
  renderKT26AnnualReport,
  createPrintIframe,
  printTransferSummaryReport,
  printSSOReport,
  printTaxReport,
  printKT26kReport
};
